import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import { useStore } from '@/lib/pocketbase'

export function useOrgUserIds() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const orgSlug = params.orgSlug

    const [orgsCollection, userOrgCollection] = useStore('orgs', 'user_org')

    const { data: orgData, isLoading: isLoadingOrg } = useLiveQuery(
        q => {
            if (!orgSlug) return undefined
            return q
                .from({ org: orgsCollection })
                .where(({ org }) => eq(org.slug, orgSlug))
                .select(({ org }) => ({ id: org.id }))
        },
        [orgSlug]
    )
    const orgId = orgData?.[0]?.id

    const { data: orgUserRecords = [], isLoading: isLoadingOrgUsers } = useLiveQuery(
        q => {
            if (!orgId) return undefined
            return q
                .from({ user_org: userOrgCollection })
                .where(({ user_org }) => eq(user_org.org, orgId))
                .select(({ user_org }) => ({
                    user: user_org.user,
                    color: user_org.color,
                    inactive_at: (user_org as unknown as { inactive_at?: string }).inactive_at,
                }))
        },
        [orgId]
    )

    const activeOrgUserRecords = useMemo(
        () => orgUserRecords.filter(r => !r.inactive_at),
        [orgUserRecords]
    )

    const orgUserIds = useMemo(
        () => activeOrgUserRecords.map(r => r.user).filter(Boolean) as string[],
        [activeOrgUserRecords]
    )

    const userColorMap = useMemo(() => {
        const map = new Map<string, string>()
        for (const r of orgUserRecords) {
            if (r.user && r.color) {
                map.set(r.user, r.color)
            }
        }
        return map
    }, [orgUserRecords])

    return {
        orgSlug,
        orgId,
        orgUserIds,
        userColorMap,
        isPending: isLoadingOrg || isLoadingOrgUsers,
    }
}
